import type { ReactNode } from "react";

/**
 * Lightweight markdown → React renderer. Handles the underwriter-note format:
 * # / ## / ### headings, **bold-only lines** as sub-headings, **inline bold**,
 * *italic*, and `-`/`*` bullet lists, plus paragraphs. XSS-safe — builds React
 * elements, never sets innerHTML. Decodes the common HTML entities in case Zoho
 * returns the note rich-text-encoded.
 */
function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Inline **bold** / *italic* → nodes. */
function inline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rem = text;
  let k = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/;
  for (;;) {
    const m = rem.match(re);
    if (!m || m.index === undefined) {
      if (rem) nodes.push(rem);
      break;
    }
    if (m.index > 0) nodes.push(rem.slice(0, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${keyBase}-b${k}`}>{m[2]}</strong>);
    else nodes.push(<em key={`${keyBase}-i${k}`}>{m[3]}</em>);
    rem = rem.slice(m.index + m[0].length);
    k++;
  }
  return nodes;
}

export function Markdown({ text }: { text: string }) {
  const src = decodeEntities(text || "").replace(/\r\n/g, "\n");
  const lines = src.split("\n");
  const blocks: ReactNode[] = [];
  let list: ReactNode[] | null = null;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push(<p key={`p${blocks.length}`}>{inline(para.join(" "), `p${blocks.length}`)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(<ul key={`u${blocks.length}`}>{list}</ul>);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    const boldHead = line.match(/^\*\*(.+?)\*\*:?$/);
    const bullet = line.match(/^[-*]\s+(.*)$/);

    if (h) {
      flushPara();
      flushList();
      const lvl = Math.min(3, h[1].length);
      const Tag = `h${lvl + 2}` as "h3" | "h4" | "h5";
      blocks.push(<Tag key={`h${blocks.length}`}>{inline(h[2], `h${blocks.length}`)}</Tag>);
    } else if (boldHead) {
      flushPara();
      flushList();
      blocks.push(<h4 key={`bh${blocks.length}`}>{boldHead[1]}</h4>);
    } else if (bullet) {
      flushPara();
      if (!list) list = [];
      list.push(<li key={`l${list.length}`}>{inline(bullet[1], `l${blocks.length}-${list.length}`)}</li>);
    } else if (line === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return <div className="md">{blocks}</div>;
}
